import type { AxePurchaseState, v2 } from '@deque/billing-service-client';
import { ProductSlugs } from '@deque/billing-utils';
import {
  Alert,
  AlertActions,
  AlertContent,
  Button,
  Combobox,
  ComboboxOption,
  DescriptionDetails,
  DescriptionList,
  DescriptionListItem,
  DescriptionTerm,
  FieldWrap,
  IconButton,
  Loader,
  Modal,
  ModalContent,
  SearchField,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TabPanel,
  Tabs
} from '@deque/cauldron-react';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { HttpError } from '~/common/api-client';
import { useAuthContext } from '~/common/contexts/auth';
import { useFeatureFlagState } from '~/common/contexts/featureFlags';
import { useProducts } from '~/common/contexts/products';
import { useAllEnterprises } from '~/common/hooks/useAllEnterprises';
import {
  createAdminSubscription,
  type SubscriptionEnvironment
} from '~/common/utils/admin-client/add-subscription';
import { addUserToEnterprise } from '~/common/utils/admin-client/add-user';
import {
  ENTERPRISE_LICENSE_COUNT,
  NO_LICENSE_SLUGS
} from '~/common/utils/admin-client/constants';
import { createEnterpriseWithSubscription } from '~/common/utils/admin-client/create-enterprise-with-subscription';
import { deleteAdminSubscription } from '~/common/utils/admin-client/delete-subscription';
import { updateAdminSubscription } from '~/common/utils/admin-client/edit-subscription';
import { editEnterpriseUser } from '~/common/utils/admin-client/edit-user';
import type { ProductData } from '~/common/utils/admin-client/get-enterprise-explorer';
import getEnterpriseExplorer, {
  SFPeopleTypes
} from '~/common/utils/admin-client/get-enterprise-explorer';
import {
  exportSecurityEventLog,
  SECURITY_EVENT_LOG_CSV_FILENAME
} from '~/common/utils/admin-client/get-security-event-log';
import billingClient, {
  AccessTypes,
  memberHasAccepted,
  memberIsPending,
  memberIsPendingUser
} from '~/common/utils/billing-client/client-v2';
import { downloadBlob } from '~/common/utils/downloadBlob';
import AddUserForm, {
  type AddUserFormData
} from '~/modules/app/components/Admin/AddUserForm';
import EditUserForm, {
  type EditUserFormData
} from '~/modules/app/components/Admin/EditUserForm';
import SubscriptionForm, {
  type SubscriptionFormData
} from '~/modules/app/components/Admin/SubscriptionForm';
import ContentToast from '~/modules/common/components/ContentToast';
import type { ErrorOrNull, OrNull } from '~/typings/utils';
import DeleteUsersPanel from './DeleteUsersPanel';
import styles from './EnterpriseExplorer.css';
import SecurityEventLogTab from './SecurityEventLogTab';
import SecurityLogExportsTab from './SecurityLogExportsTab';
import SubscriptionMemberReportModal from './SubscriptionMemberReportModal';
import SubscriptionMembersModal from './SubscriptionMembersModal';
import { deriveRootGroup } from './deriveRootGroup';
import type { SelectedEnterprise } from './types';

const PRODUCTS_WITH_APP_ADMIN = new Set<string>([
  ProductSlugs.axeMonitor,
  ProductSlugs.axeReports,
  ProductSlugs.axeAuditor,
  ProductSlugs.axeDevToolsMobile
]);

const EXPLORER_OPTION_ADD_SUBSCRIPTION = 'Add Subscription';
const EXPLORER_OPTION_VIEW_INVITED_USERS = 'View Invited Users';
const EXPLORER_OPTION_ADD_USER = 'Add User';
const EXPLORER_OPTION_VIEW_USERS = 'View Users';

// TODO: Remove once @deque/billing-service-client includes created_at on v2.Invitation
interface InvitationWithCreatedAt extends v2.Invitation {
  created_at?: string;
}

type PendingInvite = Pick<
  InvitationWithCreatedAt,
  'id' | 'email' | 'subscription_access' | 'axe_account_access' | 'created_at'
>;

interface TableRow {
  subscriptionId: string;
  product: string;
  license: string;
  expiration: string;
  environment: string;
  slug: string;
  subscription: v2.EnterpriseSubscription;
}

interface EditingSubscription {
  subscriptionId: string;
  slug: string;
  subscription: v2.EnterpriseSubscription;
}

const EXPLORER_OPTION_EDIT_USER = 'Edit User';
const EXPLORER_OPTION_DELETE_USERS = 'Delete Users';
const EXPLORER_OPTION_SUBSCRIPTION_MEMBER_REPORT = 'Subscription Member Report';
const EXPLORER_OPTION_EXPORT_SECURITY_LOGS = 'Export Security Logs';

const INSTALL_LOCATION_TO_ENV: Record<string, SubscriptionEnvironment> = {
  SaaS: 'default',
  'Private Cloud': 'private_cloud',
  'On-Prem': 'on_prem'
};

const ENV_TO_INSTALL_LOCATION: Record<string, string> = Object.fromEntries(
  Object.entries(INSTALL_LOCATION_TO_ENV).map(([loc, env]) => [env, loc])
);

function formatDateString(value: string | Date | null | undefined): string {
  return value ? String(value).split('T')[0] : '';
}

function deriveSubscriptionCounts(
  data: SubscriptionFormData & { selectedProduct: string }
): { licenseCount: number | null | undefined; unitCount: number | undefined } {
  let licenseCount: number | null | undefined;
  if (data.selectedProduct === ProductSlugs.axeLinter) {
    licenseCount = data.unlimitedLoc
      ? ENTERPRISE_LICENSE_COUNT
      : Number(data.linesOfCode) || undefined;
  } else if (NO_LICENSE_SLUGS.has(data.selectedProduct)) {
    licenseCount = null;
  } else if (data.licenseType === 'enterprise') {
    licenseCount = ENTERPRISE_LICENSE_COUNT;
  } else if (data.numberOfLicenses) {
    licenseCount = Number(data.numberOfLicenses);
  }

  const unitCount =
    data.selectedProduct === ProductSlugs.axeMonitor
      ? data.pageCount
      : undefined;

  return { licenseCount, unitCount };
}

export interface InvitedUserRow {
  key: string;
  email: string;
  isAdmin: boolean;
  productName: string;
  role: string;
  sentDate: string;
  onDelete: () => void;
  deleteLabel: string;
}

export function buildInvitedUserRows(
  invitedUsers: PendingInvite[],
  subscriptionIdToProduct: Record<string, string>,
  t: (key: string, options?: Record<string, string>) => string,
  setDeletingProduct: (
    value: OrNull<{
      invite: PendingInvite;
      subscriptionId: string | null;
      productName: string;
    }>
  ) => void
): InvitedUserRow[] {
  return invitedUsers.flatMap(invite => {
    const entries = Object.entries(invite.subscription_access);
    const sentDate = invite.created_at
      ? new Date(invite.created_at).toLocaleString()
      : t('Not available');

    if (entries.length === 0) {
      return [
        {
          key: invite.id,
          email: invite.email,
          isAdmin: invite.axe_account_access === AccessTypes.ADMIN,
          productName: t('None'),
          role: t('Not available'),
          sentDate,
          onDelete: () =>
            setDeletingProduct({
              invite,
              subscriptionId: null,
              productName: t('None')
            }),
          deleteLabel: t('Delete invitation for {{email}}', {
            email: invite.email
          })
        }
      ];
    }

    return entries.map(([subId, role]) => {
      const productName =
        subscriptionIdToProduct[subId] || t('Unknown Product');
      return {
        key: `${invite.id}-${subId}`,
        email: invite.email,
        isAdmin: invite.axe_account_access === AccessTypes.ADMIN,
        productName,
        role: role === AccessTypes.ADMIN ? t('Admin') : t('General'),
        sentDate,
        onDelete: () =>
          setDeletingProduct({
            invite,
            subscriptionId: subId,
            productName
          }),
        deleteLabel: t('Remove {{product}} from {{email}}', {
          product: productName,
          email: invite.email
        })
      };
    });
  });
}

function createTableData(
  products: Record<string, ProductData>,
  t: (key: string) => string,
  getProductBySlug: (slug: string) => { name: string } | undefined
): TableRow[] {
  return Object.entries(products).map(([key, data]) => {
    const sub = data.subscription;
    const slug = sub.product_slug || key;

    let license = t('None');
    if (NO_LICENSE_SLUGS.has(slug)) {
      license = t('N/A');
    } else if (sub.unit_count !== null) {
      license =
        sub.unit_count >= ENTERPRISE_LICENSE_COUNT
          ? t('Enterprise')
          : sub.unit_count.toString();
    } else if ('license_count' in sub && sub.license_count !== null) {
      license =
        sub.license_count >= ENTERPRISE_LICENSE_COUNT
          ? t('Enterprise')
          : sub.license_count.toString();
    }

    let expiration = t('N/A');
    if (sub.end_date) {
      const date =
        typeof sub.end_date === 'string'
          ? new Date(sub.end_date)
          : sub.end_date;
      expiration = date.toISOString().split('T')[0];
    }

    const envMap: Record<string, string> = {
      default: t('SaaS'),
      private_cloud: t('Private Cloud'),
      on_prem: t('On-Prem')
    };

    const environment = envMap[sub?.environment ?? 'default'] ?? 'N/A';

    return {
      subscriptionId: sub.id,
      product: getProductBySlug(slug)?.name || slug,
      license,
      expiration,
      environment,
      slug,
      subscription: sub
    };
  });
}

const emptyEnterprisePreload: SelectedEnterprise = {
  enterpriseMembers: [],
  enterpriseExplorer: {
    customer_name: '',
    customer_domain: '',
    id: '',
    customer_email: null,
    jira_tickets: [],
    agora_group: '',
    dqu_id: null,
    stripe_id: null,
    stripe_customer: {},
    stripe_email: null,
    auto_add_users: false,
    admins: [],
    subscription_list: {},
    products: {}
  }
};
const EnterpriseExplorer: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const token = user && user.token ? user.token : '';
  const isSuperAdmin = user?.groups?.includes('superadmin') ?? false;
  const hasAuditLog = useFeatureFlagState('audit_log_v1');
  const loaderRef = React.useRef<HTMLDivElement>(null);
  const invitedUsersLoaderRef = React.useRef<HTMLDivElement>(null);
  const { getProductBySlug } = useProducts();
  const [addSubscriptionModalOpen, setAddSubscriptionModalOpen] =
    useState(false);
  const [addSubscriptionSuccess, setAddSubscriptionSuccess] = useState(false);
  const [addSubscriptionError, setAddSubscriptionError] =
    useState<ErrorOrNull>(null);
  const [addInvitationError, setAddInvitationError] =
    useState<ErrorOrNull>(null);
  const [viewInvitedUsersModalOpen, setViewInvitedUsersModalOpen] =
    useState(false);
  const [invitedUsers, setInvitedUsers] = useState<PendingInvite[]>([]);
  const [loadingInvitedUsers, setLoadingInvitedUsers] = useState(false);
  const [errorInvitedUsers, setErrorInvitedUsers] = useState<ErrorOrNull>(null);
  const [deletingProduct, setDeletingProduct] = useState<
    OrNull<{
      invite: PendingInvite;
      subscriptionId: string | null;
      productName: string;
    }>
  >(null);
  const [deleteInvitationInProgress, setDeleteInvitationInProgress] =
    useState(false);
  const [deleteInvitationSuccess, setDeleteInvitationSuccess] = useState<
    string | false
  >(false);
  const [deleteInvitationError, setDeleteInvitationError] =
    useState<ErrorOrNull>(null);
  const [editSubscriptionModalOpen, setEditSubscriptionModalOpen] =
    useState(false);
  const [editSubscriptionSuccess, setEditSubscriptionSuccess] = useState(false);
  const [editSubscriptionError, setEditSubscriptionError] =
    useState<ErrorOrNull>(null);
  const [editingSubscription, setEditingSubscription] =
    useState<OrNull<EditingSubscription>>(null);
  const [deleteSubscriptionSuccess, setDeleteSubscriptionSuccess] =
    useState(false);
  const [deleteSubscriptionError, setDeleteSubscriptionError] =
    useState<ErrorOrNull>(null);
  const [deletingSubscription, setDeletingSubscription] =
    useState<OrNull<TableRow>>(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [activeUserCount, setActiveUserCount] = useState<OrNull<number>>(null);
  const [viewUsersModalOpen, setViewUsersModalOpen] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [confirmEditOpen, setConfirmEditOpen] = useState(false);
  const [pendingEditData, setPendingEditData] =
    useState<OrNull<SubscriptionFormData & { selectedProduct: string }>>(null);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [addUserSuccess, setAddUserSuccess] = useState(false);
  const [addUserError, setAddUserError] = useState<ErrorOrNull>(null);
  const [addUserErrors, setAddUserErrors] = useState<string[]>([]);
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  const [editUserSuccess, setEditUserSuccess] = useState(false);
  const [editUserError, setEditUserError] = useState<ErrorOrNull>(null);
  const [viewingProductUsersRow, setViewingProductUsersRow] =
    useState<OrNull<TableRow>>(null);
  const [viewingProductAdminsRow, setViewingProductAdminsRow] =
    useState<OrNull<TableRow>>(null);
  const [showDeleteUsers, setShowDeleteUsers] = useState(false);
  const [subscriptionMemberReportOpen, setSubscriptionMemberReportOpen] =
    useState(false);
  const [newEnterpriseSubModalOpen, setNewEnterpriseSubModalOpen] =
    useState(false);
  const [newEnterpriseSubSuccess, setNewEnterpriseSubSuccess] = useState(false);
  const [newEnterpriseSubError, setNewEnterpriseSubError] =
    useState<ErrorOrNull>(null);
  const [newEnterpriseInvitationError, setNewEnterpriseInvitationError] =
    useState<ErrorOrNull>(null);
  const activeProductRow = viewingProductAdminsRow ?? viewingProductUsersRow;

  const handleCloseSubscriptionMembers = () => {
    setViewingProductUsersRow(null);
    setViewingProductAdminsRow(null);
  };

  React.useEffect(() => {
    if (!addSubscriptionSuccess) {
      return;
    }
    const timer = setTimeout(() => setAddSubscriptionSuccess(false), 5000);
    return () => clearTimeout(timer);
  }, [addSubscriptionSuccess]);

  React.useEffect(() => {
    if (!editSubscriptionSuccess) {
      return;
    }
    const timer = setTimeout(() => setEditSubscriptionSuccess(false), 5000);
    return () => clearTimeout(timer);
  }, [editSubscriptionSuccess]);

  React.useEffect(() => {
    if (!deleteSubscriptionSuccess) {
      return;
    }
    const timer = setTimeout(() => setDeleteSubscriptionSuccess(false), 5000);
    return () => clearTimeout(timer);
  }, [deleteSubscriptionSuccess]);

  React.useEffect(() => {
    if (!deleteInvitationSuccess) {
      return;
    }
    const timer = setTimeout(() => setDeleteInvitationSuccess(false), 5000);
    return () => clearTimeout(timer);
  }, [deleteInvitationSuccess]);

  React.useEffect(() => {
    if (!addUserSuccess) {
      return;
    }
    const timer = setTimeout(() => setAddUserSuccess(false), 5000);
    return () => clearTimeout(timer);
  }, [addUserSuccess]);

  React.useEffect(() => {
    if (!newEnterpriseSubSuccess) {
      return;
    }
    const timer = setTimeout(() => setNewEnterpriseSubSuccess(false), 5000);
    return () => clearTimeout(timer);
  }, [newEnterpriseSubSuccess]);

  React.useEffect(() => {
    if (!editUserSuccess) {
      return;
    }
    const timer = setTimeout(() => setEditUserSuccess(false), 5000);
    return () => clearTimeout(timer);
  }, [editUserSuccess]);

  const {
    data: enterprises,
    loading: loadingEnterprises,
    error: errorEnterprises,
    refresh: refreshEnterprises
  } = useAllEnterprises({ token });
  const [selectedEnterprise, setSelectedEnterprise] =
    useState<OrNull<SelectedEnterprise>>(null);
  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [loadingEnterpriseDetails, setLoadingEnterpriseDetails] =
    useState(false);
  const [errorEnterpriseDetails, setErrorEnterpriseDetails] =
    useState<ErrorOrNull>(null);
  const [selectedOption, setSelectedOption] = useState<OrNull<string>>(null);
  const [exportSecurityLogsError, setExportSecurityLogsError] =
    useState<ErrorOrNull>(null);

  const onSearchChange = async (enterpriseId: string) => {
    setLoadingEnterpriseDetails(true);
    setErrorEnterpriseDetails(null);
    try {
      const [enterpriseMembers, enterpriseExplorer] = await Promise.all([
        billingClient.enterpriseMembers.list({
          token,
          enterpriseId: enterpriseId
        }),
        getEnterpriseExplorer.getEnterpriseExplorer({
          token,
          enterpriseId: enterpriseId
        })
      ]);
      setSelectedEnterprise({
        enterpriseMembers,
        enterpriseExplorer
      });
      setTableData(
        createTableData(enterpriseExplorer.products, t, getProductBySlug)
      );
    } catch (err) {
      setErrorEnterpriseDetails(
        err instanceof Error ? err : new Error(String(err))
      );
      setSelectedEnterprise(null);
      setTableData([]);
    } finally {
      setLoadingEnterpriseDetails(false);
    }
  };

  const editSubscriptionInitialValues = useMemo(() => {
    if (!editingSubscription) {
      return undefined;
    }
    const sub = editingSubscription.subscription;
    const isEnterpriseLicenseCount =
      sub.license_count !== null &&
      sub.license_count >= ENTERPRISE_LICENSE_COUNT;
    return {
      licenseStartDate: formatDateString(sub.start_date),
      licenseEndDate: formatDateString(sub.end_date),
      trialEndDate: formatDateString(sub.trial_expires),
      numberOfLicenses: isEnterpriseLicenseCount
        ? ''
        : sub.license_count !== null
          ? String(sub.license_count)
          : '',
      licenseType: isEnterpriseLicenseCount ? 'enterprise' : 'number',
      pageCount: sub.unit_count ?? 25000,
      installLocation:
        ENV_TO_INSTALL_LOCATION[sub.environment ?? 'default'] ?? 'SaaS',
      purchaseState: sub.purchase_state as AxePurchaseState,
      autoAddUsers: sub.auto_add_users ?? false
    };
  }, [editingSubscription]);

  const handleAddSubscription = async (
    data: SubscriptionFormData & { selectedProduct: string }
  ) => {
    if (!selectedEnterprise) {
      return;
    }

    const enterpriseId = selectedEnterprise.enterpriseExplorer.id;
    const environment =
      INSTALL_LOCATION_TO_ENV[data.installLocation] ?? 'default';
    const { licenseCount, unitCount } = deriveSubscriptionCounts(data);

    setAddSubscriptionError(null);
    setAddInvitationError(null);
    try {
      await createAdminSubscription({
        token,
        enterpriseId,
        product_slug: data.selectedProduct,
        purchase_state: data.trialEndDate ? 'trialing' : 'paid',
        ...(licenseCount !== null &&
          licenseCount !== undefined && { license_count: licenseCount }),
        ...(unitCount !== undefined && { unit_count: unitCount }),
        ...(data.licenseStartDate && { start_date: data.licenseStartDate }),
        ...(data.licenseEndDate && { end_date: data.licenseEndDate }),
        ...(data.trialEndDate && { trial_expires: data.trialEndDate }),
        environment,
        ...(data.productUrl && { product_url: data.productUrl }),
        ...(data.monitorGroupPath && { sub_group: data.monitorGroupPath }),
        ...(data.monitorV8 && { monitor_v8: data.monitorV8 })
      });
    } catch (err) {
      setAddSubscriptionError(
        err instanceof Error ? err : new Error(String(err))
      );
      return;
    }

    if (data.makeCustomerContactAdmin && data.customerEmail) {
      const inviteEmail = data.customerEmail.trim().toLowerCase();
      try {
        await billingClient.enterpriseInvitations.addMember({
          token,
          enterpriseId,
          isAdmin: true,
          email: inviteEmail
        });
        await billingClient.enterpriseInvitations.create({
          token,
          enterpriseId,
          productSlug: data.selectedProduct,
          email: inviteEmail
        });
      } catch (err) {
        setAddInvitationError(
          err instanceof Error ? err : new Error(String(err))
        );
      }
    }

    setAddSubscriptionSuccess(true);
    setAddSubscriptionModalOpen(false);
    await onSearchChange(enterpriseId);
  };

  const handleViewInvitedUsers = async (): Promise<void> => {
    if (!selectedEnterprise) {
      return;
    }
    setLoadingInvitedUsers(true);
    setErrorInvitedUsers(null);
    try {
      const enterpriseId = selectedEnterprise.enterpriseExplorer.id;
      const invitations = await billingClient.enterpriseInvitations.list({
        token,
        enterpriseId,
        isAccepted: false
      });
      // TODO: Remove cast once @deque/billing-service-client includes created_at
      const unaccepted = (invitations as InvitationWithCreatedAt[]).filter(
        inv => inv.accepted_at === null
      );
      const pending = unaccepted.map(inv => ({
        id: inv.id,
        email: inv.email,
        subscription_access: inv.subscription_access,
        axe_account_access: inv.axe_account_access,
        created_at: inv.created_at
      }));
      const sorted = pending.sort((a, b) =>
        a.email.toLowerCase().localeCompare(b.email.toLowerCase())
      );

      setInvitedUsers(sorted);
      setViewInvitedUsersModalOpen(true);
    } catch (err) {
      setErrorInvitedUsers(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoadingInvitedUsers(false);
    }
  };

  const handleDeleteInvitation = async (): Promise<void> => {
    if (!deletingProduct || !selectedEnterprise) {
      return;
    }

    setDeleteInvitationError(null);
    setDeleteInvitationInProgress(true);
    const { invite, subscriptionId } = deletingProduct;
    const enterpriseId = selectedEnterprise.enterpriseExplorer.id;
    const updatedAccess = { ...invite.subscription_access };
    if (subscriptionId) {
      delete updatedAccess[subscriptionId];
    }

    const hasRemainingSubscriptions = Object.keys(updatedAccess).length > 0;
    const hasAdminAccess = invite.axe_account_access === AccessTypes.ADMIN;
    const shouldDeleteEntireInvitation =
      !subscriptionId || (!hasRemainingSubscriptions && !hasAdminAccess);

    try {
      if (shouldDeleteEntireInvitation) {
        await billingClient.enterpriseInvitations.delete({
          token,
          enterpriseId,
          invitationId: invite.id
        });
      } else {
        await billingClient.enterpriseInvitations.updateSubscriptionAccess({
          token,
          enterpriseId,
          invitationId: invite.id,
          subscriptionAccess: updatedAccess
        });
      }
      setDeleteInvitationSuccess(
        shouldDeleteEntireInvitation
          ? t('Invitation deleted successfully')
          : t('Product removed from invitation successfully')
      );
      setDeletingProduct(null);
      await handleViewInvitedUsers();
    } catch (err) {
      setDeleteInvitationError(
        err instanceof Error ? err : new Error(String(err))
      );
    } finally {
      setDeleteInvitationInProgress(false);
    }
  };

  const handleEditSubscriptionRequest = (
    data: SubscriptionFormData & { selectedProduct: string }
  ): Promise<void> => {
    setPendingEditData(data);
    setConfirmEditOpen(true);
    return Promise.resolve();
  };

  const confirmEditSubscription = async () => {
    if (!selectedEnterprise || !editingSubscription || !pendingEditData) {
      return;
    }

    setConfirmEditOpen(false);

    const data = pendingEditData;
    const enterpriseId = selectedEnterprise.enterpriseExplorer.id;
    const environment =
      INSTALL_LOCATION_TO_ENV[data.installLocation] ?? 'default';
    const { licenseCount, unitCount } = deriveSubscriptionCounts(data);

    setEditSubscriptionError(null);
    try {
      const isExtendTrial = data.editAction === 'extend_trial';
      const hasCustomerNameChanged =
        data.customerName &&
        data.customerName !==
          selectedEnterprise.enterpriseExplorer.customer_name;
      const hasCustomerDomainChanged =
        data.customerDomain &&
        data.customerDomain !==
          selectedEnterprise.enterpriseExplorer.customer_domain;

      await updateAdminSubscription({
        token,
        enterpriseId,
        subscriptionId: editingSubscription.subscriptionId,
        purchase_state: isExtendTrial ? 'trialing' : data.purchaseState,
        ...(licenseCount !== null &&
          licenseCount !== undefined && { license_count: licenseCount }),
        ...(unitCount !== undefined && { unit_count: unitCount }),
        start_date: data.licenseStartDate,
        end_date: data.licenseEndDate,
        trial_expires: isExtendTrial ? data.trialEndDate : undefined,
        environment,
        auto_add_users: data.autoAddUsers,
        product_url: data.productUrl || undefined,
        ...(hasCustomerNameChanged && { customer_name: data.customerName }),
        ...(hasCustomerDomainChanged && {
          customer_domain: data.customerDomain
        })
      });

      setEditSubscriptionSuccess(true);
      setEditSubscriptionModalOpen(false);
      await onSearchChange(enterpriseId);
    } catch (err) {
      setEditSubscriptionError(
        err instanceof Error ? err : new Error(String(err))
      );
    }
  };

  const handleDeleteSubscription = async (force = false) => {
    if (!deletingSubscription || !selectedEnterprise) {
      return;
    }
    const enterpriseId = selectedEnterprise.enterpriseExplorer.id;
    setDeleteSubscriptionError(null);
    setActiveUserCount(null);
    setDeleteInProgress(true);
    try {
      await deleteAdminSubscription({
        token,
        enterpriseId,
        subscriptionId: deletingSubscription.subscriptionId,
        force
      });
      setDeleteSubscriptionSuccess(true);
      setDeletingSubscription(null);
      await onSearchChange(enterpriseId);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      if ((error as HttpError).status === 409) {
        setActiveUserCount(
          (error as HttpError).details?.active_user_count ?? null
        );
      } else {
        setDeleteSubscriptionError(error);
        setDeletingSubscription(null);
      }
    } finally {
      setDeleteInProgress(false);
    }
  };

  const subscriptionIdToProduct = useMemo((): Record<string, string> => {
    if (!selectedEnterprise) {
      return {};
    }
    const map: Record<string, string> = {};
    for (const [subId, slug] of Object.entries(
      selectedEnterprise.enterpriseExplorer.subscription_list
    )) {
      map[subId] = getProductBySlug(slug)?.name || t('Unknown Product');
    }
    return map;
  }, [selectedEnterprise, getProductBySlug, t]);

  const invitedUserRows = useMemo(
    () =>
      buildInvitedUserRows(
        invitedUsers,
        subscriptionIdToProduct,
        t,
        setDeletingProduct
      ),
    [invitedUsers, subscriptionIdToProduct, t, setDeletingProduct]
  );

  const deleteConfirmationMessage = useMemo(() => {
    if (!deletingProduct) {
      return '';
    }
    const { invite, subscriptionId, productName } = deletingProduct;
    const remainingCount = subscriptionId
      ? Object.keys(invite.subscription_access).length - 1
      : Object.keys(invite.subscription_access).length;
    const hasAdminAccess = invite.axe_account_access === AccessTypes.ADMIN;
    const willDeleteEntireInvitation =
      !subscriptionId || (remainingCount === 0 && !hasAdminAccess);
    return willDeleteEntireInvitation
      ? t(
          'This is the last product for {{email}}. Deleting it will remove the entire invitation. Continue?',
          { email: invite.email }
        )
      : t('Remove {{product}} from the invitation for {{email}}?', {
          product: productName,
          email: invite.email
        });
  }, [deletingProduct, t]);

  const membersWithEmail = useMemo(() => {
    if (!selectedEnterprise) {
      return [];
    }
    return selectedEnterprise.enterpriseMembers.filter(
      member => !memberIsPendingUser(member)
    );
  }, [selectedEnterprise]);

  const filteredUsers = useMemo(() => {
    if (!selectedEnterprise || !viewUsersModalOpen) {
      return [];
    }
    const enterpriseMembers = [...membersWithEmail];
    const constructedMembers = enterpriseMembers.map(member => {
      const role = member.is_admin ? t('Admin') : t('User');

      if (memberHasAccepted(member)) {
        const name = [member.first_name, member.last_name]
          .filter(Boolean)
          .join(' ');

        const latestActivity =
          member.user_activity.length > 0
            ? member.user_activity.reduce(
                (latest, activity) =>
                  activity.updated_at > latest ? activity.updated_at : latest,
                member.user_activity[0].updated_at
              )
            : undefined;

        const lastUserActivity = latestActivity
          ? new Date(latestActivity).toLocaleDateString()
          : undefined;

        return {
          id: member.user_id,
          label: name ? `${name} [${member.email}]` : member.email,
          status: t('Active'),
          role,
          lastUserActivity,
          sortKey: (name || member.email).toLowerCase()
        };
      }

      if (memberIsPending(member)) {
        return {
          id: member.invitation_id,
          label: member.email,
          status: t('Pending Invitation'),
          role,
          lastUserActivity: undefined,
          sortKey: member.email.toLowerCase()
        };
      }

      // PendingUser members are filtered out by membersWithEmail,
      // but TypeScript needs an explicit return for exhaustiveness.
      return {
        id: member.user_id,
        label: member.user_id,
        status: t('Pending'),
        role,
        lastUserActivity: undefined,
        sortKey: member.user_id.toLowerCase()
      };
    });

    const deduplicated = constructedMembers.filter(
      (entry, index, self) => self.findIndex(e => e.id === entry.id) === index
    );

    const searched = deduplicated.filter(entry => {
      if (!userSearchQuery) {
        return true;
      }
      const query = userSearchQuery.toLowerCase();
      return entry.label.toLowerCase().includes(query);
    });

    return searched.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [
    membersWithEmail,
    selectedEnterprise,
    viewUsersModalOpen,
    userSearchQuery,
    t
  ]);

  const hasAnyUserActivity = useMemo(
    () => filteredUsers.some(u => u.lastUserActivity),
    [filteredUsers]
  );

  const handleAddUser = async (data: AddUserFormData) => {
    if (!selectedEnterprise) {
      return;
    }

    const enterpriseId = selectedEnterprise.enterpriseExplorer.id;
    setAddUserError(null);
    setAddUserErrors([]);

    try {
      const result = await addUserToEnterprise({
        token,
        enterpriseId,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        isEnterpriseAdmin: data.isEnterpriseAdmin,
        products: data.products,
        productAdmins: data.productAdmins,
        sendEmail: data.sendEmail
      });

      if (result.errors && result.errors.length > 0) {
        setAddUserErrors(result.errors);
        if (result.subscriptionsAdded && result.subscriptionsAdded.length > 0) {
          setAddUserSuccess(true);
        }
      } else {
        setAddUserSuccess(true);
      }

      setAddUserModalOpen(false);
      await onSearchChange(enterpriseId);
    } catch (err) {
      setAddUserError(err instanceof Error ? err : new Error(String(err)));
      setAddUserModalOpen(false);
    }
  };

  const handleNewEnterpriseAndSubscription = async (
    data: SubscriptionFormData & { selectedProduct: string }
  ) => {
    setNewEnterpriseSubError(null);
    setNewEnterpriseInvitationError(null);

    try {
      const rootGroup = deriveRootGroup(data.customerDomain);
      const isTrial = !!data.trialEndDate;
      const environment =
        INSTALL_LOCATION_TO_ENV[data.installLocation] ?? 'default';
      const { licenseCount, unitCount } = deriveSubscriptionCounts(data);

      const { invitation_error } = await createEnterpriseWithSubscription({
        token,
        name: data.customerName,
        ...(data.customerEmail && {
          enterprise_stripe_email: data.customerEmail
        }),
        ...(rootGroup && { root_group: rootGroup }),
        auto_add_users: data.autoAddUsers,
        product_slug: data.selectedProduct,
        purchase_state: isTrial ? 'trialing' : 'paid',
        ...(licenseCount !== null &&
          licenseCount !== undefined && { license_count: licenseCount }),
        ...(unitCount !== undefined && { unit_count: unitCount }),
        start_date: data.licenseStartDate,
        end_date: data.licenseEndDate,
        ...(data.trialEndDate && {
          trial_expires: data.trialEndDate
        }),
        environment,
        ...(data.productUrl && { product_url: data.productUrl }),
        ...(data.monitorGroupPath && { sub_group: data.monitorGroupPath }),
        ...(data.monitorV8 && { monitor_v8: data.monitorV8 }),
        ...(data.customerEmail && {
          customer_email: data.customerEmail,
          make_customer_contact_admin: !!data.makeCustomerContactAdmin
        })
      });

      if (invitation_error) {
        setNewEnterpriseInvitationError(new Error(invitation_error));
      } else {
        setNewEnterpriseSubSuccess(true);
      }
      setNewEnterpriseSubModalOpen(false);
      refreshEnterprises();
    } catch (err) {
      setNewEnterpriseSubError(
        err instanceof Error ? err : new Error(String(err))
      );
    }
  };

  const handleEditUser = async (data: EditUserFormData) => {
    if (!selectedEnterprise) {
      return;
    }

    const enterpriseId = selectedEnterprise.enterpriseExplorer.id;
    setEditUserError(null);

    try {
      const result = await editEnterpriseUser({
        token,
        enterpriseId,
        userId: data.userId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        enabled: data.enabled,
        isEnterpriseAdmin: data.isEnterpriseAdmin,
        monitorEntitlements: data.monitorEntitlements
      });

      if (result.errors && result.errors.length > 0) {
        setEditUserError(new Error(result.errors.join('; ')));
      } else {
        setEditUserSuccess(true);
      }

      setEditUserModalOpen(false);
      await onSearchChange(enterpriseId);
    } catch (err) {
      setEditUserError(err instanceof Error ? err : new Error(String(err)));
      setEditUserModalOpen(false);
    }
  };

  const explorerOptions: { label: string; value: string }[] = [
    {
      label: t('Add Subscription'),
      value: EXPLORER_OPTION_ADD_SUBSCRIPTION
    },
    {
      label: t('View Invited Users'),
      value: EXPLORER_OPTION_VIEW_INVITED_USERS
    },
    {
      label: t('View Users'),
      value: EXPLORER_OPTION_VIEW_USERS
    },
    {
      label: t('Add User'),
      value: EXPLORER_OPTION_ADD_USER
    },
    {
      label: t('Edit User'),
      value: EXPLORER_OPTION_EDIT_USER
    },
    {
      label: t('Delete Users'),
      value: EXPLORER_OPTION_DELETE_USERS
    },
    {
      label: t('Subscription Member Report'),
      value: EXPLORER_OPTION_SUBSCRIPTION_MEMBER_REPORT
    },
    ...(hasAuditLog
      ? [
          {
            label: t('Export Security Logs'),
            value: EXPLORER_OPTION_EXPORT_SECURITY_LOGS
          }
        ]
      : [])
  ];

  const handleEditSubscriptionOpen = (row: TableRow) => {
    setEditingSubscription({
      subscriptionId: row.subscriptionId,
      slug: row.slug,
      subscription: row.subscription
    });
    setEditSubscriptionError(null);
    setEditSubscriptionModalOpen(true);
  };

  const handleExplorerSubmit = () => {
    if (selectedOption === EXPLORER_OPTION_ADD_SUBSCRIPTION) {
      setAddSubscriptionError(null);
      setAddSubscriptionModalOpen(true);
    } else if (selectedOption === EXPLORER_OPTION_VIEW_USERS) {
      setUserSearchQuery('');
      setViewUsersModalOpen(true);
    } else if (selectedOption === EXPLORER_OPTION_ADD_USER) {
      setAddUserError(null);
      setAddUserModalOpen(true);
    } else if (selectedOption === EXPLORER_OPTION_EDIT_USER) {
      setEditUserError(null);
      setEditUserModalOpen(true);
    } else if (selectedOption === EXPLORER_OPTION_DELETE_USERS) {
      setShowDeleteUsers(true);
      setAddSubscriptionModalOpen(false);
    } else if (selectedOption === EXPLORER_OPTION_VIEW_INVITED_USERS) {
      handleViewInvitedUsers();
    } else if (selectedOption === EXPLORER_OPTION_SUBSCRIPTION_MEMBER_REPORT) {
      setSubscriptionMemberReportOpen(true);
    } else if (selectedOption === EXPLORER_OPTION_EXPORT_SECURITY_LOGS) {
      const enterpriseId = selectedEnterprise?.enterpriseExplorer.id;
      if (!enterpriseId) {
        return;
      }
      setExportSecurityLogsError(null);
      exportSecurityEventLog({ token, enterpriseId })
        .then(blob => {
          downloadBlob(blob, SECURITY_EVENT_LOG_CSV_FILENAME);
          setExportSecurityLogsError(null);
        })
        .catch(err => {
          setExportSecurityLogsError(
            err instanceof Error ? err : new Error(String(err))
          );
        });
    }
  };

  const managementPanelRef = useRef<HTMLDivElement>(null);
  const securityLogPanelRef = useRef<HTMLDivElement>(null);
  const exportsPanelRef = useRef<HTMLDivElement>(null);
  // Mount the audit log tabs lazily on first visit, then keep them mounted so
  // their internal filter/pagination/selection state survives tab switches.
  const [auditLogTabOpened, setAuditLogTabOpened] = useState(false);
  const [exportsTabOpened, setExportsTabOpened] = useState(false);

  const editSubscriptionHttpError = editSubscriptionError as HttpError | null;

  return (
    <div className={styles.page}>
      <h1>{t('Enterprise Explorer')}</h1>

      <Tabs
        key={hasAuditLog ? 'tabs-with-audit' : 'tabs-default'}
        aria-label={t('Enterprise Explorer sections')}
        onChange={({ activeTabIndex: idx }) => {
          if (idx === 1) {
            setAuditLogTabOpened(true);
          } else if (idx === 2) {
            setExportsTabOpened(true);
          }
        }}
      >
        <Tab target={managementPanelRef}>
          {t('Enterprises & Subscriptions')}
        </Tab>
        {hasAuditLog && (
          <Tab target={securityLogPanelRef}>{t('Security Event Log')}</Tab>
        )}
        {hasAuditLog && (
          <Tab target={exportsPanelRef}>{t('Security Log Exports')}</Tab>
        )}
      </Tabs>

      <TabPanel ref={managementPanelRef}>
        <div className={styles.newEnterpriseSubButton}>
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              setNewEnterpriseSubError(null);
              setNewEnterpriseInvitationError(null);
              setNewEnterpriseSubModalOpen(true);
            }}
          >
            {t('New Enterprise & Subscription')}
          </Button>
        </div>

        {loadingEnterprises ? (
          <Loader
            label={t('Loading enterprises...')}
            ref={loaderRef}
            tabIndex={-1}
          />
        ) : (
          <>
            {errorEnterprises && (
              <ContentToast type="caution" show>
                <strong>{t('Error -')}</strong>
                <code>{errorEnterprises.message}</code>
              </ContentToast>
            )}

            <FieldWrap className={styles.fieldWrap}>
              <Combobox label={t('Search for an enterprise')} name="search">
                {enterprises?.map(enterprise => (
                  <ComboboxOption
                    key={enterprise.id}
                    value={enterprise.name}
                    onClick={() => {
                      onSearchChange(enterprise.id);
                    }}
                  >
                    {enterprise.name}
                  </ComboboxOption>
                ))}
              </Combobox>
            </FieldWrap>

            {selectedEnterprise && (
              <FieldWrap className={styles.fieldWrap}>
                <Combobox
                  label={t('Explorer Options')}
                  onSelectionChange={({ value: option }) => {
                    setSelectedOption(option as string);
                  }}
                >
                  {explorerOptions.map(option => (
                    <ComboboxOption key={option.value} value={option.value}>
                      {option.label}
                    </ComboboxOption>
                  ))}
                </Combobox>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleExplorerSubmit}
                >
                  {t('Submit')}
                </Button>
              </FieldWrap>
            )}

            {errorEnterpriseDetails && (
              <ContentToast type="caution" show>
                <strong>{t('Error loading enterprise -')}</strong>
                <code>{errorEnterpriseDetails.message}</code>
              </ContentToast>
            )}

            {errorInvitedUsers && (
              <ContentToast type="caution" show>
                <strong>{t('Error loading invited users -')}</strong>
                <code>{errorInvitedUsers.message}</code>
              </ContentToast>
            )}

            {loadingInvitedUsers && (
              <Loader
                label={t('Loading invited users...')}
                ref={invitedUsersLoaderRef}
                tabIndex={-1}
              />
            )}

            {loadingEnterpriseDetails && (
              <Loader
                label={t('Loading enterprise details...')}
                ref={loaderRef}
                tabIndex={-1}
              />
            )}

            {!loadingEnterpriseDetails && selectedEnterprise && (
              <>
                <DescriptionList>
                  <DescriptionListItem>
                    <DescriptionTerm>{t('Customer Name')}</DescriptionTerm>
                    <DescriptionDetails>
                      {selectedEnterprise.enterpriseExplorer.customer_name}
                    </DescriptionDetails>
                  </DescriptionListItem>
                  <DescriptionListItem>
                    <DescriptionTerm>{t('Customer Domain')}</DescriptionTerm>
                    <DescriptionDetails>
                      {selectedEnterprise.enterpriseExplorer.customer_domain}
                    </DescriptionDetails>
                  </DescriptionListItem>
                  <DescriptionListItem>
                    <DescriptionTerm>{t('IDP Active')}</DescriptionTerm>
                    <DescriptionDetails>{t('Active')}</DescriptionDetails>
                  </DescriptionListItem>
                  <DescriptionListItem>
                    <DescriptionTerm>
                      {t('Number of Enterprise Users')}
                    </DescriptionTerm>
                    <DescriptionDetails>
                      {membersWithEmail.length}
                    </DescriptionDetails>
                  </DescriptionListItem>
                  <DescriptionListItem>
                    <DescriptionTerm>
                      {t('Customer Email Address')}
                    </DescriptionTerm>
                    <DescriptionDetails>
                      {selectedEnterprise.enterpriseExplorer.customer_email ??
                        ''}
                    </DescriptionDetails>
                  </DescriptionListItem>
                  <DescriptionListItem>
                    <DescriptionTerm>
                      {t('Stripe Email Address')}
                    </DescriptionTerm>
                    <DescriptionDetails>
                      {selectedEnterprise.enterpriseExplorer.stripe_email ?? ''}
                    </DescriptionDetails>
                  </DescriptionListItem>
                  {selectedEnterprise.enterpriseExplorer.cs_health && (
                    <DescriptionListItem>
                      <DescriptionTerm>{t('CS Health')}</DescriptionTerm>
                      <DescriptionDetails>
                        {selectedEnterprise.enterpriseExplorer.cs_health}
                      </DescriptionDetails>
                    </DescriptionListItem>
                  )}
                  {selectedEnterprise.enterpriseExplorer.account_people_info?.[
                    SFPeopleTypes.ACCOUNT_EXEC
                  ] && (
                    <DescriptionListItem>
                      <DescriptionTerm>
                        {t('Account Executive')}
                      </DescriptionTerm>
                      <DescriptionDetails>
                        {selectedEnterprise.enterpriseExplorer.account_people_info?.[
                          SFPeopleTypes.ACCOUNT_EXEC
                        ]?.join(', ')}
                      </DescriptionDetails>
                    </DescriptionListItem>
                  )}
                  {selectedEnterprise.enterpriseExplorer.account_people_info?.[
                    SFPeopleTypes.ENGAGEMENT
                  ] && (
                    <DescriptionListItem>
                      <DescriptionTerm>
                        {t('Engagement Manager')}
                      </DescriptionTerm>
                      <DescriptionDetails>
                        {selectedEnterprise.enterpriseExplorer.account_people_info?.[
                          SFPeopleTypes.ENGAGEMENT
                        ]?.join(', ')}
                      </DescriptionDetails>
                    </DescriptionListItem>
                  )}
                  {selectedEnterprise.enterpriseExplorer.account_people_info?.[
                    SFPeopleTypes.TSA
                  ] && (
                    <DescriptionListItem>
                      <DescriptionTerm>
                        {t('Technical Support Architect')}
                      </DescriptionTerm>
                      <DescriptionDetails>
                        {selectedEnterprise.enterpriseExplorer.account_people_info?.[
                          SFPeopleTypes.TSA
                        ]?.join(', ')}
                      </DescriptionDetails>
                    </DescriptionListItem>
                  )}
                  {selectedEnterprise.enterpriseExplorer.account_people_info?.[
                    SFPeopleTypes.CS_SPECIALIST
                  ] && (
                    <DescriptionListItem>
                      <DescriptionTerm>
                        {t('Customer Success Specialist')}
                      </DescriptionTerm>
                      <DescriptionDetails>
                        {selectedEnterprise.enterpriseExplorer.account_people_info?.[
                          SFPeopleTypes.CS_SPECIALIST
                        ]?.join(', ')}
                      </DescriptionDetails>
                    </DescriptionListItem>
                  )}
                  {selectedEnterprise.enterpriseExplorer.account_people_info?.[
                    SFPeopleTypes.IMPLEMENTATION
                  ] && (
                    <DescriptionListItem>
                      <DescriptionTerm>
                        {t('Implementation Engineer')}
                      </DescriptionTerm>
                      <DescriptionDetails>
                        {selectedEnterprise.enterpriseExplorer.account_people_info?.[
                          SFPeopleTypes.IMPLEMENTATION
                        ]?.join(', ')}
                      </DescriptionDetails>
                    </DescriptionListItem>
                  )}
                  <DescriptionListItem>
                    <DescriptionTerm>{t('Offline Purchase')}</DescriptionTerm>
                    <DescriptionDetails>{t('Yes')}</DescriptionDetails>
                  </DescriptionListItem>
                </DescriptionList>

                <Table variant="border">
                  <TableHead>
                    <TableRow>
                      <TableHeader>{t('Product')}</TableHeader>
                      <TableHeader>{t('License')}</TableHeader>
                      <TableHeader>{t('Expiration')}</TableHeader>
                      <TableHeader>{t('Environment')}</TableHeader>
                      <TableHeader>{t('Actions')}</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData.map(row => (
                      <TableRow key={row.subscriptionId}>
                        <TableCell>{row.product}</TableCell>
                        <TableCell>{row.license}</TableCell>
                        <TableCell>{row.expiration}</TableCell>
                        <TableCell>{row.environment}</TableCell>
                        <TableCell>
                          <IconButton
                            icon="pencil"
                            label={t('Edit License Subscription')}
                            onClick={() => handleEditSubscriptionOpen(row)}
                          />
                          <IconButton
                            icon="eye"
                            label={t('View Users')}
                            onClick={() => {
                              setViewingProductUsersRow(row);
                              setViewingProductAdminsRow(null);
                            }}
                          />
                          {PRODUCTS_WITH_APP_ADMIN.has(row.slug) && (
                            <IconButton
                              icon="check-shield"
                              label={t('View Application Admins')}
                              onClick={() => {
                                setViewingProductAdminsRow(row);
                                setViewingProductUsersRow(null);
                              }}
                            />
                          )}
                          <IconButton
                            icon="trash"
                            label={t('Delete Subscription')}
                            onClick={() => setDeletingSubscription(row)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </>
        )}
      </TabPanel>

      {hasAuditLog && (
        <TabPanel ref={securityLogPanelRef}>
          {auditLogTabOpened && <SecurityEventLogTab />}
        </TabPanel>
      )}

      {hasAuditLog && (
        <TabPanel ref={exportsPanelRef}>
          {exportsTabOpened && <SecurityLogExportsTab />}
        </TabPanel>
      )}

      {exportSecurityLogsError && (
        <ContentToast
          type="caution"
          show
          onDismiss={() => setExportSecurityLogsError(null)}
        >
          {t('Error exporting security logs')}:{' '}
          {exportSecurityLogsError.message}
        </ContentToast>
      )}

      {addSubscriptionSuccess && (
        <ContentToast type="confirmation" show>
          {t('Subscription added successfully')}
        </ContentToast>
      )}

      {addInvitationError && (
        <ContentToast type="caution" show>
          <strong>{t('Error creating invitation -')}</strong>
          <code>{addInvitationError.message}</code>
        </ContentToast>
      )}

      {showDeleteUsers && selectedEnterprise && (
        <Modal
          heading={t('Manage Users')}
          onClose={() => setShowDeleteUsers(false)}
          show={showDeleteUsers}
          className={styles.adminModal}
        >
          <ModalContent>
            <DeleteUsersPanel
              members={selectedEnterprise.enterpriseMembers}
              enterpriseId={selectedEnterprise.enterpriseExplorer.id}
              token={token}
              onComplete={() =>
                onSearchChange(selectedEnterprise.enterpriseExplorer.id)
              }
              onClose={() => setShowDeleteUsers(false)}
            />
          </ModalContent>
        </Modal>
      )}

      {editSubscriptionSuccess && (
        <ContentToast type="confirmation" show>
          {t('Subscription updated successfully')}
        </ContentToast>
      )}

      {editSubscriptionError && (
        <ContentToast type="caution" show>
          <strong>{t('Error editing subscription -')}</strong>{' '}
          {editSubscriptionHttpError?.error_code ===
            'SEATS_IN_USE_EXCEED_LICENSE_COUNT' &&
          typeof editSubscriptionHttpError.details?.seats_to_remove === 'number'
            ? t(
                'Seats in use exceed the license count. Remove {{seatsToRemove}} seat(s) or pending invitation(s) before reducing.',
                {
                  seatsToRemove:
                    editSubscriptionHttpError.details.seats_to_remove
                }
              )
            : editSubscriptionError.message}
        </ContentToast>
      )}

      {deleteSubscriptionSuccess && (
        <ContentToast type="confirmation" show>
          {t('Subscription deleted successfully')}
        </ContentToast>
      )}

      {deleteSubscriptionError && (
        <ContentToast type="caution" show>
          <strong>{t('Error deleting subscription -')}</strong>
          <code>{deleteSubscriptionError.message}</code>
        </ContentToast>
      )}

      <Alert
        variant="warning"
        heading={t('Delete Subscription')}
        show={!!deletingSubscription}
        onClose={() => {
          setDeletingSubscription(null);
          setActiveUserCount(null);
        }}
      >
        <AlertContent>
          {activeUserCount !== null
            ? t(
                'This subscription has {{count}} active user(s). Deleting it will revoke their access. Are you sure you want to proceed?',
                { count: activeUserCount }
              )
            : t(
                'Are you sure you want to delete the subscription for {{productName}}?',
                { productName: deletingSubscription?.product }
              )}
          {deletingSubscription && (
            <DescriptionList>
              <DescriptionListItem>
                <DescriptionTerm>{t('License')}</DescriptionTerm>
                <DescriptionDetails>
                  {deletingSubscription.license}
                </DescriptionDetails>
              </DescriptionListItem>
              <DescriptionListItem>
                <DescriptionTerm>{t('Expiration')}</DescriptionTerm>
                <DescriptionDetails>
                  {deletingSubscription.expiration}
                </DescriptionDetails>
              </DescriptionListItem>
              <DescriptionListItem>
                <DescriptionTerm>{t('Environment')}</DescriptionTerm>
                <DescriptionDetails>
                  {deletingSubscription.environment}
                </DescriptionDetails>
              </DescriptionListItem>
            </DescriptionList>
          )}
        </AlertContent>
        <AlertActions>
          {activeUserCount !== null ? (
            <Button
              variant="error"
              disabled={deleteInProgress}
              onClick={() => handleDeleteSubscription(true)}
            >
              {t('Delete Anyway')}
            </Button>
          ) : (
            <Button
              variant="error"
              disabled={deleteInProgress}
              onClick={() => handleDeleteSubscription()}
            >
              {t('Delete')}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              setDeletingSubscription(null);
              setActiveUserCount(null);
            }}
          >
            {t('Cancel')}
          </Button>
        </AlertActions>
      </Alert>

      {addSubscriptionModalOpen && selectedEnterprise && (
        <Modal
          heading={t('Add Subscription')}
          onClose={() => setAddSubscriptionModalOpen(false)}
          show={addSubscriptionModalOpen}
          className={styles.adminModal}
        >
          <ModalContent>
            {addSubscriptionError && (
              <ContentToast type="caution" show>
                <strong>{t('Error adding subscription -')}</strong>
                <code>{addSubscriptionError.message}</code>
              </ContentToast>
            )}
            <SubscriptionForm
              preloadCustomerData={selectedEnterprise}
              token={token}
              submitLabel={t('Add Subscription')}
              onSubmit={handleAddSubscription}
              onCancel={() => setAddSubscriptionModalOpen(false)}
            />
          </ModalContent>
        </Modal>
      )}

      {deleteInvitationSuccess && (
        <ContentToast type="confirmation" show>
          {deleteInvitationSuccess}
        </ContentToast>
      )}

      {deleteInvitationError && (
        <ContentToast type="caution" show>
          <strong>{t('Error deleting invitation -')}</strong>
          <code>{deleteInvitationError.message}</code>
        </ContentToast>
      )}

      <Alert
        variant="warning"
        heading={t('Delete Invitation')}
        show={!!deletingProduct}
        onClose={() => setDeletingProduct(null)}
      >
        <AlertContent>{deleteConfirmationMessage}</AlertContent>
        <AlertActions>
          <Button
            variant="error"
            disabled={deleteInvitationInProgress}
            onClick={handleDeleteInvitation}
          >
            {t('Delete')}
          </Button>
          <Button variant="secondary" onClick={() => setDeletingProduct(null)}>
            {t('Cancel')}
          </Button>
        </AlertActions>
      </Alert>

      {viewInvitedUsersModalOpen && (
        <Modal
          heading={t('View Invited Users')}
          onClose={() => setViewInvitedUsersModalOpen(false)}
          show={viewInvitedUsersModalOpen}
          className={styles.invitedUsersModal}
        >
          <ModalContent>
            {invitedUsers.length === 0 ? (
              <p>{t('No pending invitations found.')}</p>
            ) : (
              <Table variant="border">
                <TableHead>
                  <TableRow>
                    <TableHeader>{t('Email')}</TableHeader>
                    <TableHeader>{t('Enterprise Admin')}</TableHeader>
                    <TableHeader>{t('Product')}</TableHeader>
                    <TableHeader>{t('Role')}</TableHeader>
                    <TableHeader>{t('Sent')}</TableHeader>
                    <TableHeader>{t('Actions')}</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitedUserRows.map(row => (
                    <TableRow key={row.key}>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.isAdmin ? t('Yes') : t('No')}</TableCell>
                      <TableCell>{row.productName}</TableCell>
                      <TableCell>{row.role}</TableCell>
                      <TableCell>{row.sentDate}</TableCell>
                      <TableCell>
                        <IconButton
                          icon="trash"
                          label={row.deleteLabel}
                          onClick={row.onDelete}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </ModalContent>
        </Modal>
      )}

      {editSubscriptionModalOpen &&
        editingSubscription &&
        selectedEnterprise && (
          <Modal
            heading={t('Edit Subscription')}
            onClose={() => setEditSubscriptionModalOpen(false)}
            show={editSubscriptionModalOpen}
            className={styles.adminModal}
          >
            <ModalContent>
              <SubscriptionForm
                preloadCustomerData={selectedEnterprise}
                token={token}
                initialProduct={editingSubscription.slug}
                productLocked={true}
                isEditMode={true}
                isSuperAdmin={isSuperAdmin}
                initialValues={editSubscriptionInitialValues}
                submitLabel={t('Save Changes')}
                onSubmit={handleEditSubscriptionRequest}
                onCancel={() => setEditSubscriptionModalOpen(false)}
              />
            </ModalContent>
          </Modal>
        )}

      <Alert
        heading={t('Confirm Subscription Update')}
        show={confirmEditOpen}
        variant="warning"
        onClose={() => setConfirmEditOpen(false)}
      >
        <AlertContent>
          {t(
            'Are you sure you want to update this subscription? This will modify billing data.'
          )}
        </AlertContent>
        <AlertActions>
          <Button variant="primary" onClick={confirmEditSubscription}>
            {t('Confirm')}
          </Button>
          <Button variant="secondary" onClick={() => setConfirmEditOpen(false)}>
            {t('Cancel')}
          </Button>
        </AlertActions>
      </Alert>

      {viewUsersModalOpen && selectedEnterprise && (
        <Modal
          heading={t('Enterprise Users')}
          onClose={() => setViewUsersModalOpen(false)}
          show={viewUsersModalOpen}
          className={`${styles.viewUsersModal} ${styles.adminModal}`}
        >
          <ModalContent>
            <SearchField
              label={t('Search users')}
              value={userSearchQuery}
              onChange={setUserSearchQuery}
            />
            <Table variant="border">
              <TableHead>
                <TableRow>
                  <TableHeader>{t('Name')}</TableHeader>
                  <TableHeader>{t('Role')}</TableHeader>
                  <TableHeader>{t('Status')}</TableHeader>
                  {hasAnyUserActivity && (
                    <TableHeader>{t('Last User Activity')}</TableHeader>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={hasAnyUserActivity ? 4 : 3}>
                      {t('No users found')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map(entry => (
                    <TableRow key={entry.id}>
                      <TableCell>{entry.label}</TableCell>
                      <TableCell>{entry.role}</TableCell>
                      <TableCell>{entry.status}</TableCell>
                      {hasAnyUserActivity && (
                        <TableCell>{entry.lastUserActivity || '--'}</TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ModalContent>
        </Modal>
      )}

      {addUserSuccess && (
        <ContentToast type="confirmation" show>
          {t('User added successfully')}
        </ContentToast>
      )}

      {addUserError && (
        <ContentToast type="caution" show>
          <strong>{t('Error adding user -')}</strong>
          <code>{addUserError.message}</code>
        </ContentToast>
      )}

      {addUserErrors.length > 0 && (
        <ContentToast type="caution" show>
          <strong>{t('Error adding user -')}</strong>
          <ul>
            {addUserErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </ContentToast>
      )}

      {addUserModalOpen && selectedEnterprise && (
        <Modal
          heading={t('Add User')}
          onClose={() => setAddUserModalOpen(false)}
          show={addUserModalOpen}
          className={styles.adminModal}
        >
          <ModalContent>
            <AddUserForm
              preloadCustomerData={selectedEnterprise}
              onSubmit={handleAddUser}
              onCancel={() => setAddUserModalOpen(false)}
            />
          </ModalContent>
        </Modal>
      )}

      {newEnterpriseSubSuccess && (
        <ContentToast type="confirmation" show>
          {t('Enterprise and subscription created successfully')}
        </ContentToast>
      )}

      {newEnterpriseSubError && !newEnterpriseSubModalOpen && (
        <ContentToast type="caution" show>
          <strong>{t('Error creating enterprise and subscription -')}</strong>
          <code>{newEnterpriseSubError.message}</code>
        </ContentToast>
      )}

      {newEnterpriseInvitationError && (
        <ContentToast type="caution" show>
          <strong>
            {t(
              'Enterprise and subscription created, but the invitation failed -'
            )}
          </strong>
          <code>{newEnterpriseInvitationError.message}</code>
        </ContentToast>
      )}

      {newEnterpriseSubModalOpen && (
        <Modal
          heading={t('New Enterprise & Subscription')}
          onClose={() => setNewEnterpriseSubModalOpen(false)}
          show={newEnterpriseSubModalOpen}
          className={styles.adminModal}
        >
          <ModalContent>
            {newEnterpriseSubError && (
              <ContentToast type="caution" show>
                <strong>{t('Error -')}</strong>
                <code>{newEnterpriseSubError.message}</code>
              </ContentToast>
            )}
            <SubscriptionForm
              preloadCustomerData={emptyEnterprisePreload}
              token={token}
              submitLabel={t('Create Enterprise & Subscription')}
              onSubmit={handleNewEnterpriseAndSubscription}
              onCancel={() => setNewEnterpriseSubModalOpen(false)}
            />
          </ModalContent>
        </Modal>
      )}

      {editUserSuccess && (
        <ContentToast type="confirmation" show>
          {t('User updated successfully')}
        </ContentToast>
      )}

      {editUserError && (
        <ContentToast type="caution" show>
          <strong>{t('Error updating user -')}</strong>
          <code>{editUserError.message}</code>
        </ContentToast>
      )}

      {editUserModalOpen && selectedEnterprise && (
        <Modal
          heading={t('Edit User')}
          onClose={() => setEditUserModalOpen(false)}
          show={editUserModalOpen}
          className={styles.adminModal}
        >
          <ModalContent>
            <EditUserForm
              preloadCustomerData={selectedEnterprise}
              token={token}
              enterpriseId={selectedEnterprise.enterpriseExplorer.id}
              onSubmit={handleEditUser}
              onCancel={() => setEditUserModalOpen(false)}
            />
          </ModalContent>
        </Modal>
      )}

      {activeProductRow && selectedEnterprise && (
        <SubscriptionMembersModal
          token={token}
          enterpriseId={selectedEnterprise.enterpriseExplorer.id}
          subscriptionId={activeProductRow.subscriptionId}
          productName={activeProductRow.product}
          productSlug={activeProductRow.slug}
          adminsOnly={viewingProductAdminsRow !== null}
          onClose={handleCloseSubscriptionMembers}
        />
      )}

      {subscriptionMemberReportOpen && selectedEnterprise && (
        <SubscriptionMemberReportModal
          token={token}
          enterpriseId={selectedEnterprise.enterpriseExplorer.id}
          onClose={() => setSubscriptionMemberReportOpen(false)}
        />
      )}
    </div>
  );
};

export default EnterpriseExplorer;
